import type { SearchFacets, SearchHit } from '@contracts';
import { duration, HOUR, MINUTE, DAY } from '../lib/time';
import { signalLabels } from '../search/state';
import type { SignalKey } from '@api/devices/contracts';

// A rule-based brief over scoped evidence. Every count is exact (from facets over
// the whole result); every example cites a retrieved record. Patterns are offered
// as hypotheses to check, never as diagnoses.

export type Tone = 'neutral' | 'attention' | 'offline' | 'unknown' | 'pattern';
export interface BriefPoint { key: string; tone: Tone; text: string; cites: string[] }
export interface Brief { headline: string; points: BriefPoint[]; coverage: string | null }

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const list = (parts: string[]) => parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
const place = (hit: SearchHit) => [hit.organizationName, hit.siteName].filter(Boolean).join(' · ') || 'an unnamed site';
const seenAt = (hit: SearchHit) => (hit.lastSeenAt ? Date.parse(hit.lastSeenAt) : null);
/** Not healthy and silent for over an hour: only then does “stopped checking in” match the evidence. */
const wentSilent = (hit: SearchHit, now: number) => hit.status !== 'healthy' && seenAt(hit) !== null && now - seenAt(hit)! > HOUR;

type Counts = Record<string, number>;
/** Values a field is restricted to by groups that constrain only that field; null when unrestricted. */
function allowed(facets: SearchFacets, field: string): Set<string> | null {
  const own = facets.query.filters.filter(g => g.clauses.every(c => c.field === field));
  if (!own.length) return null;
  const sets = own.map(g => new Set(g.clauses.flatMap(c => ('values' in c ? c.values : 'op' in c && c.op === 'unknown' ? ['unknown'] : []))));
  return sets.reduce((a, b) => new Set([...a].filter(v => b.has(v))));
}
// Facets deliberately ignore a field's own filter (so a multi-select can show what it
// would add). A brief must describe only what matched, so narrow them back down.
const restrict = (counts: Counts, keep: Set<string> | null) =>
  Object.fromEntries(Object.entries(counts).map(([k, n]) => [k, !keep || keep.has(k) ? n : 0]));

function matchedCounts(hits: SearchHit[], facets: SearchFacets) {
  const devices = hits.filter(h => h.kind === 'device');
  if (hits.length >= facets.total) {
    const tally = (key: (h: SearchHit) => string) => devices.reduce<Counts>((c, h) => ({ ...c, [key(h)]: (c[key(h)] ?? 0) + 1 }), {});
    const status = tally(h => h.status ?? 'unknown'), os = tally(h => h.os ?? 'unknown');
    const orgs = new Map<string, { id: string; title: string; count: number }>();
    for (const h of devices) {
      const o = orgs.get(h.organizationId) ?? { id: h.organizationId, title: h.organizationName ?? 'Unnamed customer', count: 0 };
      o.count += 1; orgs.set(h.organizationId, o);
    }
    const sites = new Map<string, { id: string; title: string; count: number }>();
    for (const h of devices) if (h.siteId) {
      const s = sites.get(h.siteId) ?? { id: h.siteId, title: h.siteName ?? 'Unnamed site', count: 0 };
      s.count += 1; sites.set(h.siteId, s);
    }
    const signals: Counts = {};
    for (const h of devices) for (const s of h.signals) signals[s.key] = (signals[s.key] ?? 0) + 1;
    return { status: { healthy: 0, attention: 0, offline: 0, unknown: 0, ...status }, os, never: devices.filter(h => !h.lastSeenAt).length,
      organizations: [...orgs.values()], sites: [...sites.values()], signals };
  }
  const seen = facets.query.filters.filter(g => g.clauses.every(c => c.field === 'lastSeenAt'));
  const neverAllowed = !seen.length || seen.every(g => g.clauses.some(c => c.field === 'lastSeenAt' && c.op === 'unknown'));
  const orgKeep = allowed(facets, 'organizationId'), siteKeep = allowed(facets, 'siteId');
  return {
    status: restrict(facets.status, allowed(facets, 'status')) as SearchFacets['status'],
    os: restrict(facets.os, allowed(facets, 'os')),
    never: neverAllowed ? facets.lastSeen.unknown : 0,
    organizations: facets.organizations.filter(o => !orgKeep || orgKeep.has(o.id)),
    sites: facets.sites.filter(s => !siteKeep || siteKeep.has(s.id)),
    signals: restrict(facets.signals as Counts, allowed(facets, 'signal')),
  };
}

export function buildBrief({ hits, facets, groupBy, subject, skipClusters = false, now = Date.now() }: {
  hits: SearchHit[]; facets: SearchFacets; groupBy?: 'organization' | 'site' | null; now?: number;
  /** Replaces "N devices match" when the scope is already explained, e.g. "at Acme · Main". */
  subject?: string; skipClusters?: boolean;
}): Brief {
  const total = facets.total;
  const coverage = total > hits.length ? `Counts cover all ${total} matches; examples come from the first ${hits.length}.` : null;
  const devices = hits.filter(h => h.kind === 'device');
  const onlyDevices = facets.query.kinds.length === 1 && facets.query.kinds[0] === 'device';
  if (total === 0) {
    return { headline: 'Nothing in your scope matches.', coverage: null, points: [{ key: 'empty', tone: 'neutral', cites: [],
      text: 'Loosen a filter, or check the spelling of a customer or hostname. Search only covers inventory records you can access.' }] };
  }
  if (!onlyDevices) {
    const k = facets.kinds;
    const parts = [k.organization && plural(k.organization, 'customer'), k.site && plural(k.site, 'site'), k.device && plural(k.device, 'device')].filter(Boolean) as string[];
    return { headline: `${list(parts)} ${total === 1 ? 'matches' : 'match'}.`, coverage, points: [] };
  }

  const counts = matchedCounts(hits, facets);
  const s = counts.status;
  // "Healthy" is only trusted when it is recent: stale or never-seen devices also need a look.
  const staleHealthy = devices.filter(h => h.status === 'healthy' && seenAt(h) !== null && now - seenAt(h)! > 7 * DAY);
  const unseenHealthy = devices.filter(h => h.status === 'healthy' && !h.lastSeenAt);
  // Offline needs a look only on a server; a workstation or laptop being offline is just its status (WRMM-255).
  // Counted over every match, not just the hits retrieved (at most 100).
  const offlineServers = facets.offlineServers ?? devices.filter(h => h.status === 'offline' && h.role === 'server').length;
  const problems = s.attention + offlineServers + s.unknown + staleHealthy.length + unseenHealthy.length;
  const matches = subject ? `${plural(total, 'device')} ${subject}` : total === 1 ? '1 device matches' : `${total} devices match`;
  const headline = problems === 0 && s.offline ? `${matches}; none need a look, though ${s.offline} ${s.offline === 1 ? 'is' : 'are'} offline.`
    : problems === 0 ? `${matches}, ${total === 1 ? 'reporting' : 'all reporting'} healthy and checked in this week.`
    : problems === total ? `${matches}, and ${total === 1 ? 'it needs' : 'all of them need'} a look.`
    : `${matches}; ${problems} ${problems === 1 ? 'needs' : 'need'} a look.`;
  const points: BriefPoint[] = [];
  const ids = (items: SearchHit[], max = 6) => items.slice(0, max).map(h => h.id);

  const byStatus = (status: string | null) => devices.filter(h => h.status === status);
  const statusParts: string[] = [];
  if (s.attention) statusParts.push(`${s.attention} report Attention`);
  if (s.offline) statusParts.push(`${s.offline} ${s.offline === 1 ? 'is' : 'are'} Offline`);
  if (s.unknown) statusParts.push(`${s.unknown} ${s.unknown === 1 ? 'has' : 'have'} no status reported`);
  if (statusParts.length) {
    points.push({ key: 'status', tone: s.attention ? 'attention' : offlineServers ? 'offline' : s.unknown ? 'unknown' : 'neutral',
      text: `${list(statusParts)}${s.healthy ? `; ${s.healthy} report Healthy` : ''}.`,
      cites: ids([...byStatus('attention'), ...byStatus('offline'), ...byStatus(null)]) });
  }

  // A site-wide event: several devices at one site went silent close together.
  const silent = devices.filter(h => wentSilent(h, now));
  const bySite = new Map<string, SearchHit[]>();
  for (const h of silent) if (h.siteId) bySite.set(h.siteId, [...(bySite.get(h.siteId) ?? []), h]);
  const clustered = new Set<string>();
  for (const group of skipClusters ? [] : bySite.values()) {
    if (group.length < 3) continue;
    const times = group.map(h => seenAt(h)!).sort((a, b) => a - b);
    const spread = times.at(-1)! - times[0]!;
    if (spread > 30 * MINUTE) continue;
    group.forEach(h => clustered.add(h.id));
    points.push({ key: `cluster-${group[0]!.siteId}`, tone: 'pattern', cites: ids(group),
      text: `${group.length} devices at ${place(group[0]!)} stopped checking in within ${duration(Math.max(spread, MINUTE))} of each other, about ${duration(now - times.at(-1)!)} ago. A shared cause at that site (network, power) is worth checking before individual devices.` });
  }

  if (staleHealthy.length) {
    points.push({ key: 'stale', tone: 'unknown', cites: ids(staleHealthy),
      text: `${staleHealthy.length === 1 ? '1 device reports' : `${staleHealthy.length} devices report`} Healthy but ${staleHealthy.length === 1 ? 'hasn’t' : 'haven’t'} checked in for over a week, so that status may be out of date.` });
  }
  if (counts.never) {
    points.push({ key: 'never', tone: 'unknown', cites: ids(devices.filter(h => !h.lastSeenAt)),
      text: `${counts.never === 1 ? '1 device has' : `${counts.never} devices have`} never checked in. Unknown is not the same as offline: they may be newly enrolled.` });
  }

  // Health signals from devices with the agent, most common first.
  const found = Object.entries(counts.signals).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]) as Array<[SignalKey, number]>;
  if (found.length) {
    const protection = found.filter(([k]) => k.startsWith('security.') || k === 'os.unsupported');
    points.push({ key: 'signals', tone: protection.length ? 'attention' : 'neutral',
      text: `Health signals: ${found.slice(0, 5).map(([k, n]) => `${n} ${signalLabels[k].toLowerCase()}`).join(', ')}${found.length > 5 ? `, and ${found.length - 5} more kinds` : ''}.`,
      cites: ids(devices.filter(h => h.signals.some(s => found.some(([k]) => k === s.key)))) });
  }
  const unmanaged = devices.filter(h => h.managed === false).length;
  if (unmanaged && devices.some(h => h.managed)) {
    points.push({ key: 'coverage', tone: 'unknown', cites: ids(devices.filter(h => h.managed === false)),
      text: `${unmanaged} of these ${unmanaged === 1 ? 'has' : 'have'} no agent, so ${unmanaged === 1 ? 'its' : 'their'} health signals can’t be checked.` });
  }

  const seen = devices.filter(h => h.lastSeenAt).sort((a, b) => seenAt(a)! - seenAt(b)!);
  const oldest = seen[0], newest = seen.at(-1);
  if (oldest && newest && oldest !== newest) {
    points.push({ key: 'range', tone: 'neutral', cites: [oldest.id],
      text: `Check-ins range from ${duration(now - seenAt(newest)!)} to ${duration(now - seenAt(oldest)!)} ago; the oldest is ${oldest.title}.` });
  }

  // Concentration across customers, when not already explained by a site event.
  const flagged = devices.filter(h => h.status !== 'healthy' && !(h.status === 'offline' && h.role !== 'server') && !clustered.has(h.id));
  if (flagged.length >= 3) {
    const byOrg = new Map<string, SearchHit[]>();
    for (const h of flagged) byOrg.set(h.organizationId, [...(byOrg.get(h.organizationId) ?? []), h]);
    const [top] = [...byOrg.values()].sort((a, b) => b.length - a.length);
    if (top && top.length / flagged.length >= 0.6 && byOrg.size > 1) {
      points.push({ key: 'concentration', tone: 'pattern', cites: ids(top),
        text: `${top.length} of the other ${flagged.length} devices needing a look are at ${top[0]!.organizationName ?? 'one customer'}.` });
    }
  }

  if (groupBy === 'organization' && counts.organizations.length) {
    const top = [...counts.organizations].sort((a, b) => b.count - a.count);
    points.push({ key: 'customers', tone: 'neutral', cites: [],
      text: `By customer: ${top.slice(0, 6).map(o => `${o.title} (${o.count})`).join(', ')}${top.length > 6 ? `, and ${top.length - 6} more` : ''}.` });
  } else if (groupBy === 'site' && counts.sites.length) {
    const top = [...counts.sites].sort((a, b) => b.count - a.count);
    points.push({ key: 'sites', tone: 'neutral', cites: [],
      text: `By site: ${top.slice(0, 6).map(o => `${o.title} (${o.count})`).join(', ')}${top.length > 6 ? `, and ${top.length - 6} more` : ''}.` });
  }

  const os = Object.entries(counts.os).filter(([, n]) => n > 0);
  if (os.length > 1) {
    const label: Record<string, string> = { windows: 'Windows', macos: 'macOS', linux: 'Linux', unknown: 'unknown OS' };
    points.push({ key: 'os', tone: 'neutral', cites: [],
      text: `Operating systems: ${os.sort((a, b) => b[1] - a[1]).map(([k, n]) => `${n} ${label[k]}`).join(', ')}.` });
  }
  return { headline, points, coverage };
}

/**
 * Does a device's problem look like its site's? Peers count as going quiet with it
 * only when both went silent (the same rule as fleet briefs) within 30 minutes.
 */
export function siteContext(device: SearchHit, siteHits: SearchHit[], siteTotal: number, now: number): BriefPoint {
  const others = siteHits.filter(h => h.id !== device.id);
  const place = `${device.organizationName} · ${device.siteName}`;
  const troubled = (h: SearchHit) => h.status !== 'healthy' || !h.lastSeenAt || now - Date.parse(h.lastSeenAt) > 7 * DAY;
  if (!others.length) {
    return { key: 'alone', tone: 'neutral', cites: [], text: `It’s the only device recorded at ${place}, so a site-wide cause can’t be ruled in or out from inventory.` };
  }
  const deviceSeen = seenAt(device);
  const together = deviceSeen !== null && wentSilent(device, now)
    ? others.filter(h => wentSilent(h, now) && Math.abs(seenAt(h)! - deviceSeen) <= 30 * MINUTE) : [];
  if (together.length >= 2) {
    return { key: 'site', tone: 'pattern', cites: together.slice(0, 6).map(h => h.id),
      text: `${together.length} other devices at ${place} stopped checking in within ${duration(Math.max(...together.map(h => Math.abs(seenAt(h)! - deviceSeen!)), MINUTE))} of it. This looks site-wide (network or power) rather than a fault on ${device.title}.` };
  }
  const alsoTroubled = others.filter(troubled);
  if (!alsoTroubled.length) {
    return { key: 'site', tone: troubled(device) ? 'pattern' : 'neutral', cites: [],
      text: `All ${siteTotal - 1} other devices at ${place} report healthy and checked in recently${troubled(device) ? `, so this looks specific to ${device.title}` : ''}.` };
  }
  return { key: 'site', tone: 'neutral', cites: alsoTroubled.slice(0, 6).map(h => h.id),
    text: `${alsoTroubled.length} of ${siteTotal - 1} other devices at ${place} also need a look, but not at the same time — no clear site-wide event.` };
}
